import { useTranslation } from "react-i18next";

import type { CcChangeSetSummary } from "@/api/code-companion";
import { CC_MONO_MUTED } from "@/lib/cc-trace-tokens";
import { tp } from "@/lib/plural";

/**
 * Qual change set o Trace mostra (S-064 onda 7, correção).
 *
 * A primeira versão pegava `items[0]` — o mais novo. ✅ Medido no pacote 4: existem 18
 * change sets, e **dois deles estão vazios** (22 e 23). Se um vazio fosse o mais novo, o
 * Trace abriria com uma matriz vazia sobre um pacote com dezenove objetos de história, e
 * quem olhasse concluiria que nada foi feito.
 *
 * Duas correções, e a segunda é a que importa:
 *
 * **O padrão é o mais novo COM objeto.** Vazio não é o estado interessante de nada.
 *
 * **E dá para escolher.** Com 18 change sets, "o mais novo" é uma escolha arbitrária que
 * a tela fazia em silêncio. Um pacote reprocessado tem a história boa num change set
 * antigo, e sem seletor ela era inalcançável pela interface.
 */

/**
 * O change set que a tela deve abrir: o mais novo que tem objeto.
 *
 * `null` quando nenhum tem — aí o pacote realmente não tem o que rastrear, e a tela diz
 * isso em vez de mostrar uma tabela vazia.
 */
export function defaultChangeSet(items: CcChangeSetSummary[] | undefined): number | null {
  const comObjeto = (items ?? []).find((c) => c.object_count > 0);
  return comObjeto?.id ?? null;
}

export default function ChangeSetPicker({
  items,
  value,
  onChange,
}: {
  items: CcChangeSetSummary[];
  value: number | null;
  onChange: (id: number) => void;
}) {
  const { t } = useTranslation();

  // Um change set só não é escolha: o seletor viraria enfeite.
  const comObjeto = items.filter((c) => c.object_count > 0);
  if (comObjeto.length < 2) return null;

  return (
    <label className="flex items-center gap-2">
      <span
        className="uppercase"
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: "9px",
          letterSpacing: ".1em",
          color: CC_MONO_MUTED,
        }}
      >
        {t("change set")}
      </span>
      <select
        data-testid="change-set-picker"
        value={value ?? ""}
        onChange={(e) => onChange(Number(e.target.value))}
        className="rounded border bg-card px-2 py-1"
        style={{
          borderColor: "var(--exed-card-border)",
          fontFamily: "var(--font-mono)",
          fontSize: "11px",
        }}
      >
        {comObjeto.map((c) => (
          <option key={c.id} value={c.id}>
            CS-{c.id} · {tp(t, c.object_count, "1 object", "{{n}} objects")}
          </option>
        ))}
      </select>
    </label>
  );
}
